import React from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Copy, MessageCircle, Mail } from "lucide-react";
import { toast } from "sonner";

const TYPE_LABELS = {
  phone: "phone",
  on_site: "on-site",
  meeting: "meeting",
  email: "email",
  other: "",
};

export function SendLinkDialog({ open, onClose, kind, record, url }) {
  if (!record) return null;

  const builder = record.logged_by_name || "your builder";

  const buildMessage = () => {
    if (kind === "conversation") {
      return (
        `Hi ${record.client_name},\n` +
        `I've logged a record of our ${TYPE_LABELS[record.type] || ""} conversation on ${record.date} regarding '${record.subject}'.\n` +
        `Please review and acknowledge using this link: ${url}\n` +
        `No login required — just open the link.\n` +
        `Thanks,\n${builder}\nDCS Built / SiteProApp`
      );
    }
    const desc = (record.description || "").slice(0, 80);
    return (
      `Hi ${record.client_name},\n` +
      `Variation ${record.variation_number} has been raised.\n` +
      `Re: ${desc}\n` +
      `Please review and respond (Approve or Reject) using this link: ${url}\n` +
      `No login required — just open the link.\n` +
      `Thanks,\n${builder}\nDCS Built / SiteProApp`
    );
  };

  const message = buildMessage();

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Link copied to clipboard");
    } catch {
      toast.error("Could not copy link");
    }
  };

  const openWhatsApp = () => {
    const phone = (record.client_phone || "").replace(/[^0-9]/g, "");
    const base = phone ? `https://wa.me/${phone}` : "https://wa.me/";
    window.open(`${base}?text=${encodeURIComponent(message)}`, "_blank");
  };

  const openEmail = () => {
    const subject =
      kind === "conversation"
        ? `Site Communication Record — ${record.subject}`
        : `Variation ${record.variation_number} for your review`;
    window.location.href = `mailto:${record.client_email}?subject=${encodeURIComponent(
      subject
    )}&body=${encodeURIComponent(message)}`;
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md" data-testid="send-link-dialog">
        <DialogHeader>
          <DialogTitle>Send to {record.client_name}</DialogTitle>
          <DialogDescription>
            at {record.client_email}
            {record.client_phone ? ` · ${record.client_phone}` : ""}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <Label className="text-xs text-slate-500">Link preview</Label>
            <div className="flex gap-2 mt-1">
              <Input
                readOnly
                value={url}
                className="text-xs"
                data-testid="send-link-url"
              />
              <Button
                variant="outline"
                size="icon"
                onClick={copyLink}
                data-testid="send-link-copy-btn"
              >
                <Copy className="w-4 h-4" />
              </Button>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-2">
            <Button
              onClick={openWhatsApp}
              className="bg-green-600 hover:bg-green-700"
              data-testid="send-link-whatsapp-btn"
            >
              <MessageCircle className="w-4 h-4 mr-2" /> WhatsApp
            </Button>
            <Button
              variant="outline"
              onClick={openEmail}
              data-testid="send-link-email-btn"
            >
              <Mail className="w-4 h-4 mr-2" /> Email
            </Button>
          </div>

          <p className="text-xs text-slate-500">
            SiteProApp doesn't send messages directly — copy the link or open
            WhatsApp/email with a pre-filled message to send it yourself.
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}
